import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Course } from '@/types';
import { api } from '../utils/apiInterceptor';
import { useAuth } from '../contexts/AuthContext';
import './CoursesPage.scss';

export const CoursesPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'draft' | 'published'>('all');
  const [completionFilter, setCompletionFilter] = useState<
    'all' | 'completed' | 'in-progress'
  >('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchCourses = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching courses...');
      const endpoint = '/courses/my-courses';
      const response = await api.get(endpoint);
      console.log('Courses response:', response);
      const coursesData = response.data.data || response.data;
      setCourses(Array.isArray(coursesData) ? coursesData : []);
    } catch (err: any) {
      console.error('Error fetching courses:', err);
      setError(
        err.response?.data?.message || err.message || 'Failed to load courses'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/explore');
      return;
    }

    if (!isLoading && isAuthenticated) {
      fetchCourses();
    }
  }, [isAuthenticated, isLoading, navigate]);

  const filteredCourses = courses.filter(course => {
    const matchesFilter = filter === 'all' || course.status === filter;
    const matchesSearch =
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.topic.toLowerCase().includes(searchTerm.toLowerCase());

    // Check completion status based on enrollment data
    const isCompleted =
      course.enrollments?.some(enrollment => enrollment.completedAt !== null) ||
      false;
    const matchesCompletion =
      completionFilter === 'all' ||
      (completionFilter === 'completed' && isCompleted) ||
      (completionFilter === 'in-progress' && !isCompleted);

    return matchesFilter && matchesSearch && matchesCompletion;
  });

  const difficultyLabels = {
    basic: 'Basic',
    intermediate: 'Intermediate',
    advanced: 'Advanced',
  };

  const getCompletedCount = () => {
    return courses.filter(course =>
      course.enrollments?.some(enrollment => enrollment.completedAt !== null)
    ).length;
  };

  const getInProgressCount = () => {
    return courses.filter(
      course =>
        !course.enrollments?.some(enrollment => enrollment.completedAt !== null)
    ).length;
  };

  const getColorForIndex = (index: number) => {
    const colors = [
      'blockchain',    // Green: #dcfce7, #bbf7d0
      'cooking',       // Yellow: #fef3c7, #fde68a  
      'philosophy',    // Gray: #f3f4f6, #e5e7eb
      'art',           // Purple: #e9d5ff, #d8b4fe
      'space',         // Navy: #1e293b, #334155
      'psychology',    // Pink: #fed7d7, #feb2b2
      'orange'         // Orange: #fed7aa, #fdba74
    ];
    return colors[index % colors.length];
  };

  const CourseCardSkeleton: React.FC = () => (
    <div className="course-card-skeleton">
      <div className="skeleton-header"></div>
      <div className="skeleton-content">
        <div className="skeleton-title"></div>
        <div className="skeleton-text"></div>
        <div className="skeleton-text"></div>
      </div>
    </div>
  );

  const renderPageContent = () => (
    <div className="courses-page">
      <div className="courses-container">
        {/* Page Header */}
        <div className="page-header">
          <h1>My Courses</h1>
          <p>Manage and continue your learning journey</p>
        </div>

        {/* Controls */}
        <div className="controls-section">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="search-input"
              disabled={loading}
            />
          </div>

          <div className="completion-filter-buttons">
            <button
              className={`filter-btn completion-filter ${completionFilter === 'all' ? 'active' : ''}`}
              onClick={() => setCompletionFilter('all')}
              disabled={loading}
            >
              All Courses
            </button>
            <button
              className={`filter-btn completion-filter ${completionFilter === 'in-progress' ? 'active' : ''}`}
              onClick={() => setCompletionFilter('in-progress')}
              disabled={loading}
            >
              In Progress ({loading ? '...' : getInProgressCount()})
            </button>
            <button
              className={`filter-btn completion-filter ${completionFilter === 'completed' ? 'active' : ''}`}
              onClick={() => setCompletionFilter('completed')}
              disabled={loading}
            >
              Completed ({loading ? '...' : getCompletedCount()})
            </button>
          </div>

          <button
            className="generate-btn"
            onClick={() => navigate('/generate')}
            disabled={loading}
          >
            Generate Course
          </button>
        </div>

        {/* Loading Shimmer or Content */}
        {loading ? (
          <div className="courses-grid">
            {Array.from({ length: 6 }).map((_, index) => (
              <CourseCardSkeleton key={index} />
            ))}
          </div>
        ) : error ? (
          <div className="error-state">
            <h1>Error Loading Courses</h1>
            <p>{error}</p>
            <button onClick={fetchCourses}>Try Again</button>
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="empty-state">
            <h3>
              {courses.length === 0 ? 'No courses yet' : 'No courses found'}
            </h3>
            <p>
              {courses.length === 0
                ? 'Generate your first course to get started!'
                : 'Try adjusting your search or filters.'}
            </p>
            {courses.length === 0 && (
              <button onClick={() => navigate('/generate')}>
                Generate Course
              </button>
            )}
          </div>
        ) : (
          <div className="courses-grid">
            {filteredCourses.map((course, index) => (
              <div
                key={course.id}
                className="my-course-card"
                onClick={() => navigate(`/courses/${course.id}`)}
              >
                <div
                  className={`my-course-header-visual ${getColorForIndex(index)}`}
                >
                  <div className="my-course-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span
                    className={`my-status-badge ${course.enrollments?.some(enrollment => enrollment.completedAt !== null) ? 'completed' : course.status}`}
                  >
                    {course.enrollments?.some(
                      enrollment => enrollment.completedAt !== null
                    )
                      ? '✓ COMPLETED'
                      : course.status.toUpperCase()}
                  </span>
                </div>

                <div className="my-course-content">
                  <div className="my-course-header">
                    <h3 className="my-course-title">{course.title}</h3>
                  </div>

                  <div className="my-course-description">
                    <p>{course.description}</p>
                  </div>

                  <div className="my-course-meta">
                    <span className="my-meta-item">
                      {course.estimatedHours}h
                    </span>
                    <span className="my-meta-item">
                      {course._count?.lessons || 0} lessons
                    </span>
                    <span
                      className={`my-difficulty-badge ${course.difficulty}`}
                    >
                      {difficultyLabels[course.difficulty]}
                    </span>
                  </div>

                  <div className="my-lesson-progress">
                    {(() => {
                      const totalLessons = course._count?.lessons || 0;
                      const completedLessons = course.lessons?.filter(lesson => 
                        Array.isArray(lesson.progress) 
                          ? lesson.progress.some((p: any) => p.completedAt)
                          : lesson.progress?.completedAt
                      ).length || 0;
                      const progressPercent = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;
                      
                      return (
                        <>
                          <div className="progress-info">
                            <span className="progress-text">
                              {completedLessons} of {totalLessons} lessons completed
                            </span>
                            <span className="progress-percent">{progressPercent}%</span>
                          </div>
                          <div className="progress-bar">
                            <div 
                              className="progress-fill" 
                              style={{ width: `${progressPercent}%` }}
                            ></div>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  <div className="my-current-lesson">
                    {(() => {
                      const totalLessons = course._count?.lessons || 0;
                      const completedLessons = course.lessons?.filter(lesson => 
                        Array.isArray(lesson.progress) 
                          ? lesson.progress.some((p: any) => p.completedAt)
                          : lesson.progress?.completedAt
                      ).length || 0;
                      const isCompleted = course.enrollments?.some(enrollment => enrollment.completedAt !== null);
                      
                      if (isCompleted || completedLessons === totalLessons) {
                        return (
                          <>
                            <span className="my-lesson-label">Status:</span>
                            <span className="my-lesson-title completed">Course Completed</span>
                          </>
                        );
                      } else if (completedLessons > 0) {
                        // Find the next uncompleted lesson
                        const nextLesson = course.lessons?.find(lesson => 
                          Array.isArray(lesson.progress) 
                            ? !lesson.progress.some((p: any) => p.completedAt)
                            : !lesson.progress?.completedAt
                        );
                        return (
                          <>
                            <span className="my-lesson-label">Next Lesson:</span>
                            <span className="my-lesson-title">
                              {nextLesson?.title || 'Continue learning'}
                            </span>
                          </>
                        );
                      } else {
                        // No lessons completed yet
                        const firstLesson = course.lessons?.[0];
                        return (
                          <>
                            <span className="my-lesson-label">First Lesson:</span>
                            <span className="my-lesson-title">
                              {firstLesson?.title || 'Ready to start'}
                            </span>
                          </>
                        );
                      }
                    })()}
                  </div>

                  <div className="my-course-date">
                    Created {new Date(course.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (loading || error) {
    return renderPageContent();
  }

  return renderPageContent();
};
